import { useReducer, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import {
  useV3Create,
  useV3Update,
  useV3DeleteById,
  useV3GenericGetData,
  GenericPath,
  useV3UploadDocument,
  useV3DocumentDownload,
} from "src/api/snugtotal/estate_app";
import type { File } from "src/components/file-dropzone";

import { MedicalMedicationObj } from "src/types/snugtotal";
import { downloadFileFromS3 } from "src/utils/snug";
import { reportError } from "src/utils/error-reporting";
import { useMounted } from "../use-mounted";

// Action Types
type MedicalMedicationStoreAction =
  | { type: "SET_MEDICAL_MEDICATION"; payload: MedicalMedicationObj[] }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "ADD_OR_UPDATE_MEDICAL_MEDICATION"; payload: MedicalMedicationObj }
  | { type: "DELETE_MEDICAL_MEDICATION"; payload: string }
  | { type: "SET_MEDICAL_MEDICATION_FILES_TO_UPLOAD"; payload: File[] }
  | { type: "SET_MEDICAL_MEDICATION_SAVING_NEW"; payload: boolean };

// Reducer Function
const medicalMedicationStoreReducer = (
  state: MedicalMedicationStoreState,
  action: MedicalMedicationStoreAction
): MedicalMedicationStoreState => {
  switch (action.type) {
    case "SET_MEDICAL_MEDICATION":
      return {
        ...state,
        medicalMedication: action.payload,
        medicalMedicationLoading: false,
      };

    case "SET_LOADING":
      return { ...state, medicalMedicationLoading: action.payload };

    case "ADD_OR_UPDATE_MEDICAL_MEDICATION":
      const index = state.medicalMedication.findIndex(
        (l) => l.id === action.payload.id
      );
      let updatedMedicalMedication = [...state.medicalMedication];
      if (index > -1) {
        updatedMedicalMedication[index] = action.payload;
      } else {
        updatedMedicalMedication.push(action.payload);
      }
      return { ...state, medicalMedication: updatedMedicalMedication };

    case "DELETE_MEDICAL_MEDICATION":
      return {
        ...state,
        medicalMedication: state.medicalMedication.filter(
          (l) => l.id !== action.payload
        ),
      };
    case "SET_MEDICAL_MEDICATION_FILES_TO_UPLOAD":
      return { ...state, medicalMedicationsFilestoUpload: action.payload };
    case "SET_MEDICAL_MEDICATION_SAVING_NEW":
      return { ...state, medicalMedicationSavingNew: action.payload };
    default:
      return state;
  }
};

// Initial State
interface MedicalMedicationStoreState {
  medicalMedication: MedicalMedicationObj[];
  medicalMedicationLoading: boolean;
  medicalMedicationSavingNew: boolean;
  medicalMedicationsFilestoUpload: File[];
}

// Hook
export const useMedicalMedicationStore = (ud_id: string | null) => {
  
  const isMounted = useMounted();
  const [state, dispatch] = useReducer(medicalMedicationStoreReducer, {
    medicalMedication: [],
    medicalMedicationLoading: true,
    medicalMedicationsFilestoUpload: [],
    medicalMedicationSavingNew: false,
  });

  const deleteMedicalMedicationById = useV3DeleteById(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const fetchMedicalMedicationData = useV3GenericGetData<
    MedicalMedicationObj[]
  >(GenericPath.GENERIC_ENTITY_LIST_CREATE_V3);
  const createMedicalMedication = useV3Create<MedicalMedicationObj>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const updateMedicalMedication = useV3Update<MedicalMedicationObj>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const downloadDocument = useV3DocumentDownload(
    GenericPath.GENERIC_DOCUMENT_DOWNLOAD_V3
  );
  const uploadDocument = useV3UploadDocument<MedicalMedicationObj>(
    GenericPath.GENERIC_DOCUMENT_CREATE_V3
  );
  const deleteDocumentById = useV3DeleteById(
    GenericPath.GENERIC_DOCUMENT_DELETE_V3
  );

  /// DOCUMENTS

  const handleMedicalMedicationFilesUpload = useCallback(
    async (entityId: string) => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      const files = state.medicalMedicationsFilestoUpload;
      let remaining = files;
      const totalFiles = files.length;
      let successCount = 0;
      let failCount = 0;
      for (let index = 0; index < totalFiles; index++) {
        const currentFile = files[index];

        try {
          const res = await uploadDocument(
            {
              ud_id: ud_id,
              entity: "medications",
              entity_id: entityId,
            },
            currentFile
          );
          if (res) {
            successCount++;

            // update state
            dispatch({
              type: "ADD_OR_UPDATE_MEDICAL_MEDICATION",
              payload: res,
            });

            remaining = remaining.filter(
              (_file) => _file.path !== currentFile.path
            );
            dispatch({
              type: "SET_MEDICAL_MEDICATION_FILES_TO_UPLOAD",
              payload: remaining,
            });

            const successMessage = `${successCount} of ${totalFiles} files uploaded successfully`;
            toast.success(successMessage);
          } else {
            failCount++;
            const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
            // Call your toast function here, e.g:
            toast.error(errorMessage);
            reportError(new Error(`Document Error: Failed to upload document for entity_id: ${entityId}`));
          }
        } catch (error) {
          failCount++;
          const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
          // Call your toast function here, e.g:
          toast.error(errorMessage);

          console.error(errorMessage);
          reportError(new Error(`Document Error: Failed to upload document for entity_id: ${entityId} with message "${error}"`));
        }
      }
    },
    [state.medicalMedicationsFilestoUpload, ud_id, uploadDocument]
  );

  const handleDownloadMedicalMedicationDocument = async (
    medicalMedication_id: string,
    document_id: string | null
  ) => {
    if (!document_id || !medicalMedication_id || !ud_id) {
      return Promise.reject("Missing required parameters");
    }
    toast.success("Downloading file...");

    const params: any = {
      document_id: document_id,
      entity: "medications",
      entity_id: medicalMedication_id,
      ud_id: ud_id,
    };

    downloadDocument(params)
      .then((res: any) => {
        if (res) {
          // download File from s3 res and get file name from mathcing document id in medicalMedication objects' documents array
          downloadFileFromS3(
            res,
            state.medicalMedication
              ?.find((p) => p.id === medicalMedication_id)
              ?.documents?.find((d) => d.document_id === document_id)
              ?.document_name || "Snug_Download.error"
          );
        } else {
          reportError(new Error(`Document Error: Failed to get document with message "No response"`));
        }
      })
      .catch((err) => {
        toast.error("Failed to download file");
        reportError(new Error(`Document Error: Failed to get document with message "${err}"`));
      });
  };

  const handleDeleteMedicalMedicationDocument = useCallback(
    async (
      medicalMedication_id: string,
      document_id: string | null
    ): Promise<void> => {
      if (!document_id || !medicalMedication_id || !ud_id) {
        return Promise.reject("Missing required parameters");
      }
      
      try {
        dispatch({ type: "SET_MEDICAL_MEDICATION_SAVING_NEW", payload: true });

        await deleteDocumentById({
          ud_id: ud_id,
          entity: "medications",
          entity_id: medicalMedication_id,
          document_id: document_id,
        });
        if (isMounted()) {
          // remove the document object formm the document array in the medicalMedication object
          const updatedMedicalMedication = state.medicalMedication.map((p) => {
            if (p.id === medicalMedication_id) {
              return {
                ...p,
                documents: p.documents
                  ? p.documents.filter((d) => d.document_id !== document_id)
                  : [],
              };
            }
            return p;
          });
          dispatch({
            type: "SET_MEDICAL_MEDICATION",
            payload: updatedMedicalMedication,
          });
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to delete document");
        throw err;
      } finally {
        if (isMounted()) {
          dispatch({
            type: "SET_MEDICAL_MEDICATION_SAVING_NEW",
            payload: false,
          });
        }
      }
    },
    [isMounted, state.medicalMedication, deleteDocumentById, ud_id]
  );

  const handleMedicalMedicationFilesDrop = useCallback(
    (newFiles: File[]): void => {
      dispatch({
        type: "SET_MEDICAL_MEDICATION_FILES_TO_UPLOAD",
        payload: [...state.medicalMedicationsFilestoUpload, ...newFiles],
      }); //this may not be needed
    },
    [state.medicalMedicationsFilestoUpload]
  );

  const handleMedicalMedicationFileRemove = useCallback(
    (file: File): void => {
      const updatedFiles = state.medicalMedicationsFilestoUpload.filter(
        (_file) => _file.path !== file.path
      );
      dispatch({
        type: "SET_MEDICAL_MEDICATION_FILES_TO_UPLOAD",
        payload: updatedFiles,
      });
    },
    [state.medicalMedicationsFilestoUpload]
  );

  const handleMedicalMedicationFilesRemoveAll = useCallback((): void => {
    dispatch({ type: "SET_MEDICAL_MEDICATION_FILES_TO_UPLOAD", payload: [] });
  }, []);

  /// END DOCUMENTS

  const fetchMedicalMedicationItems = useCallback(
    async (ud_id: string) => {
      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const response = await fetchMedicalMedicationData({
          ud_id: ud_id, //bleh. REFACTOR IN v3
          entity: "medications",
        });

        if (isMounted()) {
          dispatch({ type: "SET_MEDICAL_MEDICATION", payload: response });
        }
      } catch (error) {
        console.error(error);
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [fetchMedicalMedicationData, isMounted]
  );

  const handleAddOrUpdateMedicalMedicationSingleCall = useCallback(
    async (
      medicalMedicationItem: MedicalMedicationObj
    ): Promise<MedicalMedicationObj | null> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      try {
        const isUpdate = Boolean(medicalMedicationItem.id);
        let response;

        if (isUpdate) {
          // Update logic
          response = await updateMedicalMedication(
            {
              ud_id: ud_id,
              entity: "medications",
              entity_id: medicalMedicationItem.id || undefined,
            },
            medicalMedicationItem,
            true // Assuming you want to perform a partial update
          );
        } else {
          // Create logic
          response = await createMedicalMedication(
            { ud_id: ud_id, entity: "medications" }, // Adjust according to your actual API parameter requirements
            medicalMedicationItem
          );
        }
        dispatch({
          type: "ADD_OR_UPDATE_MEDICAL_MEDICATION",
          payload: response,
        });
        return response;
      } catch (error) {
        toast.error("Failed to save Medical Medication item.");
        console.error(error);
        return null;
      }
    },
    [ud_id, createMedicalMedication, updateMedicalMedication]
  );

  const handleAddOrUpdateMedicalMedication = useCallback(
    async (medicalMedicationItem: MedicalMedicationObj) => {
      dispatch({ type: "SET_MEDICAL_MEDICATION_SAVING_NEW", payload: true });
      if (state.medicalMedicationsFilestoUpload.length > 0) {
        try {
          const res = await handleAddOrUpdateMedicalMedicationSingleCall(
            medicalMedicationItem
          );
          if (res) {
            await handleMedicalMedicationFilesUpload(res.id || "");
            return res;
          }
          return null;
        } catch (error) {
          toast.error(
            "Failed to add or edit Medical Medication data. Please try again."
          );
          console.error(error);
          throw error;
        } finally {
          dispatch({
            type: "SET_MEDICAL_MEDICATION_SAVING_NEW",
            payload: false,
          });
        }
      } else {
        try {
          return handleAddOrUpdateMedicalMedicationSingleCall(
            medicalMedicationItem
          );
        } catch (error) {
          toast.error(
            "Failed to add or edit Medical Medication data. Please try again."
          );
          console.error(error);
          throw error;
        } finally {
          dispatch({
            type: "SET_MEDICAL_MEDICATION_SAVING_NEW",
            payload: false,
          });
        }
      }
    },
    [
      handleAddOrUpdateMedicalMedicationSingleCall,
      state.medicalMedicationsFilestoUpload,
      handleMedicalMedicationFilesUpload,
    ]
  );

  const handleDeleteMedicalMedication = useCallback(
    async (id: string): Promise<void> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      dispatch({ type: "SET_MEDICAL_MEDICATION_SAVING_NEW", payload: true });
      try {
        if (!ud_id) {
          return Promise.reject("No ud_id provided");
        }
        if (!id) {
          return Promise.reject("No id provided");
        }
        await deleteMedicalMedicationById({
          ud_id: ud_id,
          entity: "medications",
          entity_id: id || undefined,
        });
        dispatch({ type: "DELETE_MEDICAL_MEDICATION", payload: id });
      } catch (error) {
        toast.error("Failed to delete item");
        console.error(error);
      } finally {
        dispatch({ type: "SET_MEDICAL_MEDICATION_SAVING_NEW", payload: false });
      }
    },
    [deleteMedicalMedicationById, ud_id]
  );

  useEffect(() => {
    if (ud_id) {
      fetchMedicalMedicationItems(ud_id); // Fetch all medicalMedication items
    }
  }, [ fetchMedicalMedicationItems, ud_id]);

  return {
    medicalMedication: state.medicalMedication,
    medicalMedicationLoading: state.medicalMedicationLoading,
    medicalMedicationSavingNew: state.medicalMedicationSavingNew,
    medicalMedicationsFilestoUpload: state.medicalMedicationsFilestoUpload,
    handleAddOrUpdateMedicalMedication,
    handleDeleteMedicalMedication,
    handleMedicalMedicationFileRemove,
    handleMedicalMedicationFilesDrop,
    handleMedicalMedicationFilesRemoveAll,
    handleDownloadMedicalMedicationDocument,
    handleDeleteMedicalMedicationDocument,
    handleMedicalMedicationFilesUpload,
  };
};
